/**
 * Das linhas da planilha aos contatos que entram — e aos que não entram, com o
 * número da linha e o motivo.
 *
 * Fica sem React e sem rede: a regra de "o que é uma linha válida" se testa sem
 * montar tela, e é a mesma nos dois adaptadores.
 */

import type { LinhaImportada, NovoContato, RecusaDaImportacao, Sexo, TipoImportavel } from './tipos';

/** Linhas por importação. Acima disso a planilha se divide em partes. */
export const LIMITE_DE_LINHAS = 2000;

export interface PlanilhaParaImportar {
    colunas: string[];
    linhas: string[][];
}

/** `dd/mm/aaaa` ou `aaaa-mm-dd` → `aaaa-mm-dd`. Data que não existe (31/02) é inválida. */
export function dataIso(bruto: string): string | null {
    const v = bruto.trim();
    let ano: number; let mes: number; let dia: number;
    const br = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (br) { dia = Number(br[1]); mes = Number(br[2]); ano = Number(br[3]); }
    else if (iso) { ano = Number(iso[1]); mes = Number(iso[2]); dia = Number(iso[3]); }
    else return null;
    const d = new Date(Date.UTC(ano, mes - 1, dia));
    if (d.getUTCFullYear() !== ano || d.getUTCMonth() !== mes - 1 || d.getUTCDate() !== dia) return null;
    return `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/** "R$ 1.234,56" → 123456. Texto que não é valor devolve `null`. */
export function centavosDe(bruto: string): number | null {
    const limpo = bruto.replace(/R\$/gi, '').replace(/\s/g, '');
    if (!/^\d{1,3}(\.\d{3})*(,\d{1,2})?$|^\d+(,\d{1,2})?$|^\d+(\.\d{1,2})?$/.test(limpo)) return null;
    const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
    const valor = Number(normalizado);
    return Number.isFinite(valor) ? Math.round(valor * 100) : null;
}

function sexoDe(bruto: string): Sexo {
    const v = bruto.trim().toLowerCase();
    if (v === 'f' || v.startsWith('fem')) return 'feminino';
    if (v === 'm' || v.startsWith('masc')) return 'masculino';
    if (v.startsWith('outr')) return 'outro';
    return 'nao-informado';
}

export function montarImportacao(
    tipo: TipoImportavel,
    planilha: PlanilhaParaImportar,
    mapa: Record<string, string>,
): { validas: LinhaImportada[]; recusadas: RecusaDaImportacao[] } {
    const validas: LinhaImportada[] = [];
    const recusadas: RecusaDaImportacao[] = [];
    const telefonesVistos = new Map<string, number>();

    const valor = (linha: string[], chave: string): string => {
        const coluna = mapa[chave];
        if (!coluna) return '';
        return (linha[planilha.colunas.indexOf(coluna)] ?? '').trim();
    };

    planilha.linhas.slice(0, LIMITE_DE_LINHAS).forEach((linha, indice) => {
        /* A linha 1 é o cabeçalho: a primeira pessoa está na linha 2, que é o
           número que a clínica vê quando abre a planilha. */
        const numero = indice + 2;
        const recusar = (motivo: string) => { recusadas.push({ linha: numero, motivo }); };

        const nome = valor(linha, 'nome');
        const telefone = valor(linha, 'tel');
        const email = valor(linha, 'email');
        if (!nome) return recusar('Falta o nome.');
        if (tipo !== 'profissional' && !telefone) return recusar('Falta o telefone.');
        if (tipo === 'profissional' && !email) return recusar('Falta o e-mail — é com ele que a pessoa cria a conta.');
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return recusar(`E-mail inválido: ${email}.`);

        const digitos = telefone.replace(/\D/g, '');
        if (digitos) {
            const anterior = telefonesVistos.get(digitos);
            if (anterior) return recusar(`Telefone repetido: é o mesmo da linha ${anterior}.`);
            telefonesVistos.set(digitos, numero);
        }

        const nascimentoBruto = valor(linha, 'nasc');
        const nascimento = nascimentoBruto ? dataIso(nascimentoBruto) : undefined;
        if (nascimentoBruto && !nascimento) return recusar(`Data de nascimento inválida: ${nascimentoBruto}.`);

        const valorBruto = valor(linha, 'valor');
        const centavos = valorBruto ? centavosDe(valorBruto) : undefined;
        if (valorBruto && centavos === null) return recusar(`Valor estimado inválido: ${valorBruto}.`);

        const opcional = (chave: string, campo: keyof NovoContato): Partial<NovoContato> => {
            const v = valor(linha, chave);
            return v ? { [campo]: v } : {};
        };

        const contato: NovoContato = {
            tipo,
            nome,
            telefone,
            ...(email ? { email } : {}),
            ...(nascimento ? { nascimento } : {}),
            ...(valor(linha, 'sexo') ? { sexo: sexoDe(valor(linha, 'sexo')) } : {}),
            ...(centavos !== undefined && centavos !== null ? { valorEstimadoCentavos: centavos } : {}),
            ...opcional('cpf', 'cpf'),
            ...opcional('cep', 'cep'),
            ...opcional('endereco', 'rua'),
            ...opcional('cidade', 'cidade'),
            ...opcional('obs', 'observacoes'),
            ...opcional('origem', 'origem'),
            ...opcional('interesse', 'interesse'),
            ...opcional('responsavel', 'responsavelNome'),
            ...opcional('papel', 'especialidades'),
            ...opcional('registro', 'registro'),
        };
        validas.push({ linha: numero, contato });
    });

    if (planilha.linhas.length > LIMITE_DE_LINHAS) {
        recusadas.push({
            linha: LIMITE_DE_LINHAS + 2,
            motivo: `A planilha tem ${planilha.linhas.length} linhas; entram ${LIMITE_DE_LINHAS} por vez. Importe o restante numa segunda planilha.`,
        });
    }

    return { validas, recusadas };
}
